import React, { useEffect, useMemo, useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Calendar as CalendarIcon, Clock, ExternalLink, Ban, RotateCcw, Pencil,
  Plus, Trash2, X, AlertTriangle, Search, UserRound,
} from "lucide-react";
import { API, authHeaders, formatApiErrorDetail } from "./api";
import { SectionShell } from "./ui";
import {
  typeIcon, typeStyle, fmtDMY, APPOINTMENT_TYPES, APPOINTMENT_LENGTHS,
} from "./CustomerAppointments";

const StatBox = ({ label, count, tone, testid }) => {
  const toneCls = {
    gold: "border-gold/40 bg-gold-soft/15 text-gold-dark",
    forest: "border-forest/15 bg-cream-3 text-forest/70",
    red: "border-rose-200 bg-rose-50 text-rose-700",
  }[tone] || "border-forest/15 bg-cream-3 text-forest/70";
  return (
    <div data-testid={testid} className={`border rounded-lg p-3 ${toneCls}`}>
      <div className="flex items-center justify-between">
        <span className="text-[10.5px] tracking-[0.18em] uppercase font-bold opacity-80">{label}</span>
        <CalendarIcon size={14} strokeWidth={1.8} />
      </div>
      <p className="font-serif text-2xl mt-1 tabular-nums leading-none">{count}</p>
    </div>
  );
};

const SCOPES = [
  { v: "today", label: "Today" },
  { v: "week", label: "This week" },
  { v: "all", label: "All" },
];

const buildEmptyPayload = () => {
  const d = new Date();
  const iso = d.toISOString().slice(0, 10);
  return {
    customer_id: "",
    customer_name: "",
    title: "",
    date: iso,
    time: "10:00",
    length_minutes: 30,
    type: "Meeting",
    note: "",
  };
};

const CustomerPicker = ({ customers, value, nameValue, onPick, onFreeTypeName }) => {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState(value ? "existing" : (nameValue ? "non_customer" : "existing"));
  const wrapRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const selected = useMemo(
    () => customers.find((c) => c.id === value) || null,
    [customers, value]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return customers.slice(0, 50);
    return customers
      .filter((c) =>
        (c.name || "").toLowerCase().includes(q) ||
        (c.email || "").toLowerCase().includes(q) ||
        (c.company || "").toLowerCase().includes(q)
      )
      .slice(0, 50);
  }, [customers, query]);

  return (
    <div className="space-y-2" data-testid="appt-customer-picker">
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => { setMode("existing"); onFreeTypeName(""); }}
          data-testid="appt-customer-mode-existing"
          className={`px-3 py-1.5 text-[11.5px] font-semibold tracking-tight rounded-full border transition-colors ${
            mode === "existing"
              ? "bg-forest text-cream border-forest"
              : "bg-white border-forest/20 text-forest/70 hover:border-forest"
          }`}
        >
          Existing customer
        </button>
        <button
          type="button"
          onClick={() => { setMode("non_customer"); onPick(""); }}
          data-testid="appt-customer-mode-free"
          className={`px-3 py-1.5 text-[11.5px] font-semibold tracking-tight rounded-full border transition-colors ${
            mode === "non_customer"
              ? "bg-forest text-cream border-forest"
              : "bg-white border-forest/20 text-forest/70 hover:border-forest"
          }`}
        >
          Not a customer yet
        </button>
      </div>

      {mode === "existing" ? (
        <div className="relative" ref={wrapRef}>
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            data-testid="appt-customer-select"
            className="w-full flex items-center justify-between gap-2 bg-white border border-forest/15 px-3 py-2 text-sm text-left focus:border-gold focus:outline-none hover:border-forest/40"
          >
            <span className={selected ? "text-forest" : "text-forest/40"}>
              {selected ? (selected.name + (selected.company ? ` · ${selected.company}` : "")) : "Choose a customer…"}
            </span>
            <UserRound size={14} strokeWidth={1.8} className="text-forest/50" />
          </button>
          {open && (
            <div className="absolute z-30 top-full left-0 right-0 mt-1 bg-cream border border-forest/15 rounded shadow-lg">
              <div className="relative border-b border-forest/10">
                <Search size={13} strokeWidth={1.8} className="absolute left-3 top-1/2 -translate-y-1/2 text-forest/40" />
                <input
                  autoFocus
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search by name, email or company…"
                  data-testid="appt-customer-search"
                  className="w-full bg-transparent pl-9 pr-3 py-2 text-sm focus:outline-none"
                />
              </div>
              <div className="max-h-64 overflow-y-auto" data-lenis-prevent>
                {filtered.length === 0 ? (
                  <p className="px-3 py-6 text-[12.5px] text-sage text-center">No matching customers.</p>
                ) : filtered.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => { onPick(c.id); setOpen(false); setQuery(""); }}
                    data-testid={`appt-customer-option-${c.id}`}
                    className={`w-full text-left px-3 py-2 hover:bg-cream-3 border-b border-forest/5 last:border-0 ${
                      value === c.id ? "bg-cream-3" : ""
                    }`}
                  >
                    <p className="text-[13px] font-semibold text-forest truncate">{c.name || "—"}</p>
                    {(c.email || c.company) && (
                      <p className="text-[11.5px] text-sage truncate">{c.company || c.email}</p>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <input
          type="text"
          value={nameValue}
          onChange={(e) => onFreeTypeName(e.target.value)}
          data-testid="appt-customer-free-name"
          placeholder="Type a name"
          className="w-full bg-white border border-forest/15 px-3 py-2 text-sm focus:border-gold focus:outline-none"
        />
      )}
    </div>
  );
};

const AppointmentModal = ({ initial, customers, onClose, onSaved }) => {
  const [form, setForm] = useState(() => {
    if (!initial) return buildEmptyPayload();
    return {
      customer_id: initial.customer_id || "",
      customer_name: initial.customer_name || "",
      title: initial.title || "",
      date: initial.date || buildEmptyPayload().date,
      time: initial.time || "10:00",
      length_minutes: initial.length_minutes || 30,
      type: APPOINTMENT_TYPES.includes(initial.type) ? initial.type : "Meeting",
      note: initial.note || "",
    };
  });
  const [saving, setSaving] = useState(false);
  const [overlap, setOverlap] = useState(null); // { overlaps: [] }

  const update = (k) => (e) => setForm((f) => ({
    ...f,
    [k]: k === "length_minutes" ? Number(e.target.value) : e.target.value,
  }));

  const buildPayload = () => ({
    customer_id: form.customer_id || null,
    customer_name: form.customer_id ? "" : form.customer_name.trim(),
    title: form.title.trim(),
    date: form.date,
    time: form.time,
    length_minutes: Number(form.length_minutes) || 30,
    type: form.type,
    note: form.note,
  });

  const validate = () => {
    if (!form.customer_id && !form.customer_name.trim()) {
      toast.error("Pick a customer or type a name"); return false;
    }
    if (!form.title.trim()) { toast.error("Title is required"); return false; }
    if (!form.date) { toast.error("Date is required"); return false; }
    if (!form.time) { toast.error("Time is required"); return false; }
    return true;
  };

  const persist = async ({ force = false } = {}) => {
    setSaving(true);
    try {
      const url = initial
        ? `${API}/admin/appointments/${initial.id}${force ? "?force=true" : ""}`
        : `${API}/admin/appointments${force ? "?force=true" : ""}`;
      const method = initial ? "put" : "post";
      const r = await axios[method](url, buildPayload(), authHeaders());
      // Non-throwing success path (axios throws on 409 by default, but 409
      // can also arrive as 2xx-adjacent in some setups — keep this guard.)
      if (r.status === 409 && r.data?.detail === "overlap") {
        setOverlap({ overlaps: r.data.overlaps || [] });
        return;
      }
      toast.success(initial ? "Appointment updated" : "Appointment added");
      onSaved();
    } catch (e) {
      if (e.response?.status === 409 && e.response.data?.detail === "overlap") {
        setOverlap({ overlaps: e.response.data.overlaps || [] });
        return;
      }
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save appointment");
    } finally {
      setSaving(false);
    }
  };

  const submit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    setOverlap(null);
    persist();
  };

  return (
    <div className="fixed inset-0 z-[55] bg-black/50 flex items-start justify-center p-4 overflow-y-auto" data-testid="appt-modal" data-lenis-prevent>
      <div className="bg-cream w-full max-w-xl rounded-lg shadow-2xl mt-10 mb-10">
        <div className="flex items-center justify-between px-6 py-5 border-b border-forest/10">
          <h3 className="font-serif text-2xl text-forest">
            {initial ? "Edit appointment" : "New appointment"}
          </h3>
          <button type="button" onClick={onClose} data-testid="appt-modal-close"
            className="p-1.5 text-forest/60 hover:text-forest hover:bg-cream-3 rounded">
            <X size={18} strokeWidth={1.8} />
          </button>
        </div>
        <form onSubmit={submit} className="px-6 py-6 space-y-5" data-testid="appt-modal-form">
          <div>
            <span className="text-[11px] tracking-[0.14em] uppercase font-bold text-forest/70">
              Customer <span className="text-red-600">*</span>
            </span>
            <div className="mt-2">
              <CustomerPicker
                customers={customers}
                value={form.customer_id}
                nameValue={form.customer_name}
                onPick={(id) => setForm((f) => ({ ...f, customer_id: id, customer_name: "" }))}
                onFreeTypeName={(name) => setForm((f) => ({ ...f, customer_id: "", customer_name: name }))}
              />
            </div>
          </div>

          <label className="block">
            <span className="text-[11px] tracking-[0.14em] uppercase font-bold text-forest/70">Title <span className="text-red-600">*</span></span>
            <input type="text" value={form.title} onChange={update("title")} data-testid="appt-modal-title"
              placeholder="e.g. Document check"
              className="mt-1 w-full bg-white border border-forest/15 px-3 py-2 text-sm focus:border-gold focus:outline-none" />
          </label>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-[11px] tracking-[0.14em] uppercase font-bold text-forest/70">Date (DD/MM/YYYY) <span className="text-red-600">*</span></span>
              <input type="date" value={form.date} onChange={update("date")} data-testid="appt-modal-date"
                className="mt-1 w-full bg-white border border-forest/15 px-3 py-2 text-sm focus:border-gold focus:outline-none" />
              {form.date && <p className="text-[11px] text-sage mt-1">{fmtDMY(form.date)}</p>}
            </label>
            <label className="block">
              <span className="text-[11px] tracking-[0.14em] uppercase font-bold text-forest/70">Start time <span className="text-red-600">*</span></span>
              <input type="time" value={form.time} onChange={update("time")} data-testid="appt-modal-time"
                className="mt-1 w-full bg-white border border-forest/15 px-3 py-2 text-sm focus:border-gold focus:outline-none" />
            </label>
            <label className="block">
              <span className="text-[11px] tracking-[0.14em] uppercase font-bold text-forest/70">Length</span>
              <select value={form.length_minutes} onChange={update("length_minutes")} data-testid="appt-modal-length"
                className="mt-1 w-full bg-white border border-forest/15 px-3 py-2 text-sm focus:border-gold focus:outline-none">
                {APPOINTMENT_LENGTHS.map((n) => <option key={n} value={n}>{n} min</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-[11px] tracking-[0.14em] uppercase font-bold text-forest/70">Type</span>
              <select value={form.type} onChange={update("type")} data-testid="appt-modal-type"
                className="mt-1 w-full bg-white border border-forest/15 px-3 py-2 text-sm focus:border-gold focus:outline-none">
                {APPOINTMENT_TYPES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </label>
          </div>

          <label className="block">
            <span className="text-[11px] tracking-[0.14em] uppercase font-bold text-forest/70">Short note</span>
            <textarea rows={3} value={form.note} onChange={update("note")} data-testid="appt-modal-note"
              className="mt-1 w-full bg-white border border-forest/15 px-3 py-2 text-sm focus:border-gold focus:outline-none resize-y" />
          </label>

          <div className="flex gap-2 pt-2">
            <button type="submit" disabled={saving} data-testid="appt-modal-save" className="btn-gold disabled:opacity-50">
              {saving ? "Saving…" : (initial ? "Save changes" : "Add appointment")}
            </button>
            <button type="button" onClick={onClose} data-testid="appt-modal-cancel" className="btn-ghost-dark">Cancel</button>
          </div>
        </form>
      </div>

      {overlap && (
        <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center px-4" data-testid="appt-overlap-dialog">
          <div className="bg-cream max-w-md w-full p-6 rounded-lg shadow-2xl">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-amber-100 rounded-full">
                <AlertTriangle size={20} strokeWidth={1.8} className="text-amber-700" />
              </div>
              <div className="flex-1">
                <h3 className="font-serif text-xl text-forest">Time clash</h3>
                <p className="text-[13px] text-sage mt-1">
                  This slot clashes with {overlap.overlaps.length} other scheduled appointment{overlap.overlaps.length > 1 ? "s" : ""}.
                  You can still save if you want to.
                </p>
              </div>
            </div>
            <div className="mt-4 space-y-2 max-h-56 overflow-y-auto" data-lenis-prevent>
              {overlap.overlaps.map((o) => (
                <div key={o.id} className="bg-white border border-forest/15 rounded px-3 py-2 text-[12.5px]" data-testid="appt-overlap-item">
                  <p className="font-semibold text-forest">{o.title}</p>
                  <p className="text-sage">
                    {o.customer_name || "—"} · {fmtDMY(o.date)} · {o.time} · {o.length_minutes} min
                  </p>
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-5 justify-end">
              <button type="button" onClick={() => setOverlap(null)} data-testid="appt-overlap-cancel" className="btn-ghost-dark">Change time</button>
              <button type="button" onClick={() => persist({ force: true })} disabled={saving} data-testid="appt-overlap-confirm"
                className="btn-gold disabled:opacity-50">
                {saving ? "Saving…" : "Save anyway"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const AppointmentsSection = ({ onNavigate }) => {
  const [scope, setScope] = useState("today");
  const [typeFilter, setTypeFilter] = useState("all");
  const [includeCancelled, setIncludeCancelled] = useState(true);
  const [data, setData] = useState({ appointments: [], summary: { today: 0, total_scheduled: 0, total_cancelled: 0, total: 0 } });
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | appointment object

  const reload = () => {
    setLoading(true);
    const params = new URLSearchParams({ scope, include_cancelled: String(includeCancelled) });
    axios.get(`${API}/admin/appointments?${params.toString()}`, authHeaders())
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Could not load appointments"))
      .finally(() => setLoading(false));
  };
  useEffect(reload, [scope, includeCancelled]);

  // Fetch the customer directory once for the picker.
  useEffect(() => {
    axios.get(`${API}/admin/customers`, authHeaders())
      .then(({ data }) => setCustomers(data || []))
      .catch(() => {});
  }, []);

  const rows = useMemo(() => {
    const list = data.appointments || [];
    if (typeFilter === "all") return list;
    return list.filter((r) => r.type === typeFilter);
  }, [data.appointments, typeFilter]);

  const cancel = async (a) => {
    if (!window.confirm(`Cancel "${a.title}" for ${a.customer_name || "customer"}?`)) return;
    try {
      await axios.post(`${API}/admin/appointments/${a.id}/cancel`, {}, authHeaders());
      toast.success("Appointment cancelled");
      reload();
    } catch { toast.error("Could not cancel"); }
  };

  const reopen = async (a) => {
    try {
      await axios.post(`${API}/admin/appointments/${a.id}/reopen`, {}, authHeaders());
      toast.success("Appointment restored");
      reload();
    } catch { toast.error("Could not restore"); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.title}" for ${a.customer_name || "customer"}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/appointments/${a.id}`, authHeaders());
      toast.success("Appointment deleted");
      reload();
    } catch { toast.error("Could not delete"); }
  };

  const openCustomer = (a) => {
    if (!onNavigate || !a.customer_id) return;
    onNavigate("customers", { focusId: a.customer_id, source: "appointments", tab: "appointments" });
  };

  const s = data.summary || {};

  return (
    <SectionShell
      title="Appointments"
      subtitle="Upcoming meetings, calls and signings across every customer."
    >
      <div className="flex items-center justify-end mb-5">
        <button
          type="button"
          onClick={() => setEditing("new")}
          data-testid="appts-new-btn"
          className="inline-flex items-center gap-2 bg-forest hover:bg-forest-soft text-cream px-4 py-2.5 rounded text-[11px] font-bold tracking-[0.2em] uppercase transition-colors"
        >
          <Plus size={14} strokeWidth={2.2} /> New appointment
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <StatBox label="Today" count={s.today || 0} tone="gold" testid="appts-stat-today" />
        <StatBox label="Scheduled" count={s.total_scheduled || 0} tone="forest" testid="appts-stat-scheduled" />
        <StatBox label="Cancelled" count={s.total_cancelled || 0} tone="red" testid="appts-stat-cancelled" />
        <StatBox label="Total" count={s.total || 0} tone="forest" testid="appts-stat-total" />
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="flex gap-1.5 flex-wrap">
          {SCOPES.map((f) => (
            <button
              key={f.v}
              type="button"
              onClick={() => setScope(f.v)}
              data-testid={`appts-scope-${f.v}`}
              className={`px-3 py-1.5 text-[11.5px] font-semibold tracking-tight rounded-full border transition-colors ${
                scope === f.v
                  ? "bg-forest text-cream border-forest"
                  : "bg-white border-forest/20 text-forest/70 hover:border-forest"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            data-testid="appts-type-filter"
            className="bg-white border border-forest/15 px-3 py-1.5 text-[12px] rounded focus:border-gold focus:outline-none"
          >
            <option value="all">All types</option>
            {APPOINTMENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <label className="flex items-center gap-2 text-[12px] text-forest/70">
            <input type="checkbox" checked={includeCancelled} onChange={(e) => setIncludeCancelled(e.target.checked)}
              data-testid="appts-include-cancelled" />
            Show cancelled
          </label>
        </div>
      </div>

      {loading ? (
        <p className="text-sage">Loading appointments…</p>
      ) : rows.length === 0 ? (
        <div className="bg-white border border-forest/10 rounded-xl p-10 text-center" data-testid="appts-global-empty">
          <CalendarIcon size={28} strokeWidth={1.4} className="mx-auto text-forest/30 mb-3" />
          <p className="font-serif text-lg text-forest mb-1">
            {scope === "today" ? "Nothing scheduled for today"
              : scope === "week" ? "Nothing scheduled this week"
              : "No appointments yet"}
          </p>
          <p className="text-[13px] text-sage mb-5">
            Click <strong>New appointment</strong> to book one.
          </p>
          <button type="button" onClick={() => setEditing("new")} data-testid="appts-empty-new"
            className="btn-gold inline-flex items-center gap-2">
            <Plus size={14} strokeWidth={2.2} /> New appointment
          </button>
        </div>
      ) : (
        <div className="bg-white border border-forest/10 rounded-xl overflow-hidden">
          <div className="hidden md:grid grid-cols-[9rem_minmax(0,1fr)_minmax(0,1fr)_8rem_8rem_7rem] gap-3 px-4 py-2.5 bg-cream-3 border-b border-forest/10 text-[10.5px] tracking-[0.14em] uppercase font-bold text-forest/60">
            <span>Date / Time</span>
            <span>Title</span>
            <span>Customer</span>
            <span>Type</span>
            <span>Length</span>
            <span className="text-right">Actions</span>
          </div>
          <div className="divide-y divide-forest/10">
            {rows.map((a, i) => {
              const Icon = typeIcon(a.type);
              const isCancelled = a.status === "cancelled";
              return (
                <div
                  key={a.id}
                  data-testid={`appts-global-row-${i + 1}`}
                  className={`grid grid-cols-1 md:grid-cols-[9rem_minmax(0,1fr)_minmax(0,1fr)_8rem_8rem_7rem] gap-3 px-4 py-3 items-center transition-opacity ${
                    isCancelled ? "opacity-55 bg-cream-3/40" : ""
                  }`}
                >
                  <div className={`text-[12.5px] ${isCancelled ? "text-forest/50" : "text-forest"}`}>
                    <p className="font-semibold tabular-nums">{fmtDMY(a.date)}</p>
                    <p className="text-forest/60 tabular-nums">{a.time}</p>
                  </div>
                  <div className="min-w-0">
                    <p className={`font-semibold text-[13.5px] truncate ${isCancelled ? "line-through text-forest/50" : "text-forest"}`}>
                      {a.title}
                    </p>
                    {a.note && (
                      <p className={`text-[12px] truncate ${isCancelled ? "text-forest/40" : "text-sage"}`}>{a.note}</p>
                    )}
                  </div>
                  <div className="min-w-0">
                    {a.customer_id ? (
                      <button
                        type="button"
                        onClick={() => openCustomer(a)}
                        data-testid={`appts-global-customer-${i + 1}`}
                        className="group inline-flex items-center gap-1 text-[13px] font-semibold text-forest hover:text-gold-dark truncate"
                      >
                        <span className="truncate">{a.customer_name || "—"}</span>
                        <ExternalLink size={12} strokeWidth={1.8} className="opacity-50 group-hover:opacity-100 shrink-0" />
                      </button>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[13px] font-semibold text-forest/80 truncate" data-testid={`appts-global-customer-${i + 1}`}>
                        <span className="truncate">{a.customer_name || "—"}</span>
                        <span className="inline-flex items-center text-[9.5px] font-bold tracking-[0.1em] uppercase px-1.5 py-0.5 border rounded bg-cream-3 text-forest/60 border-forest/15 shrink-0">Lead</span>
                      </span>
                    )}
                    {a.customer_company && (
                      <p className="text-[11.5px] text-sage truncate">{a.customer_company}</p>
                    )}
                  </div>
                  <div>
                    <span className={`inline-flex items-center gap-1 text-[10px] font-bold tracking-[0.1em] uppercase px-2 py-0.5 border rounded ${typeStyle(a.type)}`}>
                      <Icon size={10} strokeWidth={2} />
                      {a.type}
                    </span>
                  </div>
                  <div className="text-[12.5px] text-forest/70 inline-flex items-center gap-1">
                    <Clock size={11} strokeWidth={1.8} />
                    {a.length_minutes} min
                    {isCancelled && (
                      <span className="ml-1 inline-flex items-center text-[10px] font-bold tracking-[0.1em] uppercase px-2 py-0.5 border rounded bg-rose-50 text-rose-700 border-rose-200">Cancelled</span>
                    )}
                  </div>
                  <div className="flex gap-1 justify-end">
                    <button type="button" onClick={() => setEditing(a)} data-testid={`appts-global-edit-${i + 1}`}
                      className="p-1.5 text-forest/60 hover:text-forest hover:bg-cream-3 rounded" title="Edit">
                      <Pencil size={14} strokeWidth={1.8} />
                    </button>
                    {!isCancelled ? (
                      <button type="button" onClick={() => cancel(a)} data-testid={`appts-global-cancel-${i + 1}`}
                        className="p-1.5 text-amber-600/80 hover:text-amber-800 hover:bg-amber-50 rounded" title="Cancel">
                        <Ban size={14} strokeWidth={1.8} />
                      </button>
                    ) : (
                      <button type="button" onClick={() => reopen(a)} data-testid={`appts-global-restore-${i + 1}`}
                        className="p-1.5 text-forest/60 hover:text-forest hover:bg-cream-3 rounded" title="Restore">
                        <RotateCcw size={14} strokeWidth={1.8} />
                      </button>
                    )}
                    <button type="button" onClick={() => remove(a)} data-testid={`appts-global-delete-${i + 1}`}
                      className="p-1.5 text-rose-500/80 hover:text-rose-700 hover:bg-rose-50 rounded" title="Delete">
                      <Trash2 size={14} strokeWidth={1.8} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {editing && (
        <AppointmentModal
          initial={editing === "new" ? null : editing}
          customers={customers}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }}
        />
      )}
    </SectionShell>
  );
};
